// src/components/PaymentModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X, ChevronLeft, Truck, Lock, CheckCircle, Loader2,
  CreditCard, Wallet, Package, AlertCircle, Store,
  ArrowRight, ShieldCheck, Ticket, Gift, User, MapPin,
  Percent, Tag, Sparkles, Search, Globe
} from 'lucide-react';
import { UserProfile } from '../../types';
import { doc, getDoc, collection, addDoc, serverTimestamp, updateDoc, increment, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';

const isVite = typeof import.meta !== 'undefined' && import.meta.env;
const getEnvVar = (key: string): string => {
  if (isVite) return import.meta.env[`VITE_${key}`] || import.meta.env[`REACT_APP_${key}`] || '';
  return process.env[`REACT_APP_${key}`] || process.env[`VITE_${key}`] || '';
};

// ============================================
// 🟢 HOUSE ATELIER — Scoop & Stone seller id
// ============================================
const ATELIER_ID = 'scoop-and-stone-atelier';
const ATELIER_NAME = 'Scoop & Stone';

// 🟢 Fallback delivery options for the atelier (and any seller without methods).
// These kick in when users/{businessId}.deliveryMethods is empty.
const DEFAULT_DELIVERY_METHODS = [
  { id: 'paxi',      label: 'Paxi (3–5 days)',    price: 60,  duration: '3–5 business days', enabled: true },
  { id: 'courier',   label: 'Courier (1–2 days)', price: 120, duration: '1–2 business days', enabled: true },
  { id: 'overnight', label: 'Overnight Express',  price: 200, duration: 'Next business day', enabled: true },
  { id: 'collect',   label: 'Collect in store',   price: 0,   duration: 'Same day',          enabled: true },
];

// ============================================
// 🟢 PAYSTACK HOOK
// ============================================
const usePaystack = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (window.PaystackPop || document.querySelector('script[src*="paystack.co/v1/inline.js"]')) {
      if (window.PaystackPop) setLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => { if (window.PaystackPop) setLoaded(true); };
    document.body.appendChild(script);
  }, []);

  return loaded;
};

// ============================================
// 🟢 PAYPAL HOOK
// ============================================
const usePayPal = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (window.paypal) {
      setLoaded(true);
      return;
    }

    const checkInterval = setInterval(() => {
      if (window.paypal) {
        clearInterval(checkInterval);
        setLoaded(true);
      }
    }, 200);

    return () => clearInterval(checkInterval);
  }, []);

  return loaded;
};

declare global {
  interface Window {
    PaystackPop: any;
    google: any;
    ApplePaySession?: any;
    paypal: any;
  }
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  cart: any[];
  onCheckoutComplete: (details: any) => void;
  profile: UserProfile | null;
}

interface Discount {
  id: string;
  name: string;
  code: string;
  discountType: 'percentage' | 'fixed' | 'bogo';
  discountValue: number;
  minPurchase?: number;
  maxUses?: number;
  usageCount: number;
  isActive: boolean;
  expiryDate?: number;
  businessId: string;
  workspaceId: string;
  appliesTo: 'all' | 'specific_products' | 'collection';
  productIds?: string[];
  collectionId?: string;
}

interface ScratchCard {
  id: string;
  name: string;
  description?: string;
  prize: string;
  totalScratches: number;
  remainingScratches: number;
  scratchCount: number;
  isActive: boolean;
  expiryDate?: number;
  businessId: string;
  workspaceId: string;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen, onClose, cart, onCheckoutComplete, profile
}) => {
  const paystackLoaded = usePaystack();
  const paypalLoaded = usePayPal();

  const [groupedCart, setGroupedCart] = useState<{ [brandId: string]: any[] }>({});
  const [activeBrandId, setActiveBrandId] = useState<string | null>(null);
  const [activeBrandSettings, setActiveBrandSettings] = useState<any | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [sellerSubaccountCode, setSellerSubaccountCode] = useState<string | null>(null);
  const [sellerHasPayout, setSellerHasPayout] = useState<boolean>(false);

  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [scratchCards, setScratchCards] = useState<ScratchCard[]>([]);
  const [appliedDiscount, setAppliedDiscount] = useState<Discount | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [selectedScratchCard, setSelectedScratchCard] = useState<ScratchCard | null>(null);
  const [scratchPrizeRevealed, setScratchPrizeRevealed] = useState(false);
  const [scratchDiscountAmount, setScratchDiscountAmount] = useState(0);
  const [isScratching, setIsScratching] = useState(false);

  // 🟢 SPLIT INTO 3 STEPS FOR MOBILE
  const [step, setStep] = useState<'delivery' | 'address' | 'payment'>('delivery');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'apple_pay' | 'paypal' | 'makapay'>('card');
  const [isApplePaySupported, setIsApplePaySupported] = useState(false);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [phoneCode, setPhoneCode] = useState('+27');

  const [isMinimized, setIsMinimized] = useState(false);
  const [isGoogleReady, setIsGoogleReady] = useState(false);

  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [isVerifyingExternal, setIsVerifyingExternal] = useState(false);

  const streetInputRef = useRef<HTMLInputElement>(null);

  const [shippingDetails, setShippingDetails] = useState({
    name: profile?.displayName || '',
    email: profile?.email || '',
    street: '', city: '', code: '', phone: profile?.phoneNumber || '',
    deliveryMethod: '', shippingCost: 0
  });

  const activeVoucher = (profile as any)?.activeVoucher || null;

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  useEffect(() => {
    if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      setIsApplePaySupported(true);
    }
  }, []);

  const getPaystackKey = (): string => {
    let key = getEnvVar('PAYSTACK_PUBLIC_KEY');
    if (!key) key = 'pk_live_7985b67599e69e640b5f64f870c93b8c77208617';
    return key;
  };

  useEffect(() => {
    if (!pendingOrderId) return;
    const unsubscribe = onSnapshot(doc(db, 'orders', pendingOrderId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.status === 'paid' || data.status === 'completed') {
          setPaymentSuccess(true);
          setIsProcessing(false);
          setIsMinimized(false);
          showToast('✅ Payment confirmed! Order complete.');

          setTimeout(() => {
            onCheckoutComplete({
              ...shippingDetails,
              method: data.paymentMethod || 'external',
              transactionId: data.paymentReference || pendingOrderId,
              brandCleared: activeBrandId,
              ...data
            });
            onClose();
          }, 2000);
        } else if (data.status === 'failed' || data.status === 'cancelled') {
          setIsProcessing(false);
          setIsMinimized(false);
          showToast('❌ Payment failed or was cancelled.');
          setPendingOrderId(null);
        }
      }
    });
    return () => unsubscribe();
  }, [pendingOrderId, activeBrandId, shippingDetails, onCheckoutComplete, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get('orderId');
    const status = params.get('status');
    const reference = params.get('reference');

    if (orderId && status === 'success') {
      setPendingOrderId(orderId);
      setIsVerifyingExternal(true);
      const verifyOrder = async () => {
        try {
          const orderDoc = await getDoc(doc(db, 'orders', orderId));
          if (orderDoc.exists()) {
            const data = orderDoc.data();
            if (data.status === 'paid' || data.status === 'completed') {
              setPaymentSuccess(true);
              setIsProcessing(false);
              setIsMinimized(false);
              showToast('✅ Payment confirmed!');

              setTimeout(() => {
                onCheckoutComplete({
                  ...shippingDetails,
                  method: data.paymentMethod || 'external',
                  transactionId: reference || orderId,
                  brandCleared: activeBrandId,
                  ...data
                });
                onClose();
              }, 2000);
            }
          }
        } catch (error) {
          console.error('Error verifying order:', error);
        } finally {
          setIsVerifyingExternal(false);
          window.history.replaceState({}, '', window.location.pathname);
        }
      };
      verifyOrder();
    } else if (orderId && status === 'cancelled') {
      showToast('❌ Payment cancelled.');
      setIsProcessing(false);
      setIsMinimized(false);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isOpen, shippingDetails, onCheckoutComplete, onClose]);

  useEffect(() => {
    if (!isOpen || !activeBrandId) return;

    const loadDiscountsAndScratchCards = async () => {
      try {
        const discountsQuery = query(
          collection(db, "discounts"),
          where("businessId", "==", activeBrandId),
          where("isActive", "==", true)
        );
        const discountSnap = await getDocs(discountsQuery);
        const loadedDiscounts = discountSnap.docs.map(d => ({ id: d.id, ...d.data() } as Discount));

        const now = Date.now();
        const validDiscounts = loadedDiscounts.filter(d => {
          if (d.expiryDate && d.expiryDate < now) return false;
          if (d.maxUses && d.usageCount >= d.maxUses) return false;
          return true;
        });
        setDiscounts(validDiscounts);

        const scratchQuery = query(
          collection(db, "scratchCards"),
          where("businessId", "==", activeBrandId),
          where("isActive", "==", true)
        );
        const scratchSnap = await getDocs(scratchQuery);
        const loadedScratchCards = scratchSnap.docs
          .map(d => ({ id: d.id, ...d.data() } as ScratchCard))
          .filter(card => card.remainingScratches > 0);
        setScratchCards(loadedScratchCards);

      } catch (error) {
        console.error("Error loading discounts:", error);
      }
    };
    loadDiscountsAndScratchCards();
  }, [isOpen, activeBrandId]);

  useEffect(() => {
    if (isOpen && cart.length > 0) {
      const groups = cart.reduce((acc, item) => {
        const bId = item.businessId || item.brandId || item.authorId || item.userId || 'unknown';
        if (!acc[bId]) acc[bId] = [];
        acc[bId].push(item);
        return acc;
      }, {} as { [key: string]: any[] });

      setGroupedCart(groups);
      const firstBrandId = Object.keys(groups)[0];
      setActiveBrandId(firstBrandId);
    }
  }, [isOpen, cart]);

  useEffect(() => {
    const fetchSettings = async () => {
      if (!activeBrandId || activeBrandId === 'unknown') return;
      setSettingsLoading(true);
      try {
        const snap = await getDoc(doc(db, "users", activeBrandId));
        if (snap.exists()) {
          const data = snap.data();
          setActiveBrandSettings(data);

          if (data.subaccountCode && data.subaccountStatus === 'verified') {
            setSellerSubaccountCode(data.subaccountCode);
            setSellerHasPayout(true);
          } else {
            setSellerSubaccountCode(null);
            setSellerHasPayout(false);
          }
        } else {
          // 🟢 No doc — but if it's the atelier, treat it as a valid seller
          // with the default delivery methods so checkout isn't blocked.
          if (activeBrandId === ATELIER_ID) {
            setActiveBrandSettings({
              businessName: ATELIER_NAME,
              displayName: ATELIER_NAME,
              brandName: ATELIER_NAME,
              workspaceType: 'jewellery',
              verified: true,
              freeShippingThreshold: 1500,
              deliveryMethods: DEFAULT_DELIVERY_METHODS,
              isFallback: true,
            });
            setSellerSubaccountCode(null);
            setSellerHasPayout(false);
          } else {
            setActiveBrandSettings({ error: "NOT_FOUND" });
          }
        }
      } catch (e) {
        console.error("Settings error:", e);
      } finally {
        setSettingsLoading(false);
      }
    };

    if (isOpen) {
      fetchSettings();
      setStep('delivery');
      setSelectedPaymentMethod('card');
      setPaymentSuccess(false);
      setIsMinimized(false);
      setIsProcessing(false);
      setAppliedDiscount(null);
      setDiscountCode('');
      setDiscountError(null);
      setScratchPrizeRevealed(false);
      setSelectedScratchCard(null);
      setPendingOrderId(null);
    }
  }, [activeBrandId, isOpen]);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsGoogleReady(true);
      return;
    }
    const apiKey = getEnvVar('GOOGLE_MAPS_API_KEY');
    if (!apiKey) return;
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsGoogleReady(true);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  useEffect(() => {
    if (!streetInputRef.current || !isGoogleReady || step !== 'address') return;
    try {
      const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current, {
        componentRestrictions: { country: ['za'] },
        fields: ['address_components', 'formatted_address', 'geometry', 'name'],
        types: ['address'],
      });

      const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Enter') e.preventDefault(); };
      streetInputRef.current.addEventListener('keydown', handleKeyDown);

      const listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && place.address_components) {
          let street = '';
          let city = '';
          let code = '';
          for (const component of place.address_components) {
            const types = component.types;
            if (types.includes('street_number')) street = component.long_name + ' ' + street;
            if (types.includes('route')) street = street + component.long_name;
            if (types.includes('locality')) city = component.long_name;
            if (types.includes('postal_code')) code = component.long_name;
          }
          if (place.formatted_address && !street) street = place.formatted_address;
          setShippingDetails(prev => ({
            ...prev,
            street: street || prev.street,
            city: city || prev.city,
            code: code || prev.code,
          }));
        }
      });
      return () => {
        if (streetInputRef.current) streetInputRef.current.removeEventListener('keydown', handleKeyDown);
        if (window.google?.maps?.event) window.google.maps.event.removeListener(listener);
      };
    } catch (error) {
      console.warn('Error initializing Google Places:', error);
    }
  }, [isGoogleReady, step]);

  // Calculations
  const activeItems = activeBrandId ? groupedCart[activeBrandId] || [] : [];
  const baseSubtotal = activeItems.reduce((acc, item) => acc + (item.price * (item.quantity || 1)), 0);

  const is100Off = activeVoucher && (activeVoucher.includes('100%') || activeVoucher.includes('FREE ITEM'));
  const isHalfOff = activeVoucher && activeVoucher.includes('50%');
  const isFlat30 = activeVoucher && activeVoucher.includes('30%');
  const isFlat15 = activeVoucher && activeVoucher.includes('15%');

  let voucherDiscount = 0;
  if (is100Off) voucherDiscount = baseSubtotal;
  else if (isHalfOff) voucherDiscount = (baseSubtotal * 0.5);
  else if (isFlat30) voucherDiscount = (baseSubtotal * 0.30);
  else if (isFlat15) voucherDiscount = (baseSubtotal * 0.15);

  let discountCodeAmount = 0;
  let discountType: 'percentage' | 'fixed' | 'bogo' | null = null;

  const getEligibleItems = () => {
    if (!appliedDiscount) return activeItems;
    if (appliedDiscount.appliesTo === 'all') return activeItems;
    if (appliedDiscount.appliesTo === 'specific_products') return activeItems.filter(item => appliedDiscount.productIds?.includes(item.id));
    if (appliedDiscount.appliesTo === 'collection') return activeItems.filter(item => item.collectionId === appliedDiscount.collectionId);
    return activeItems;
  };

  if (appliedDiscount) {
    const eligibleItems = getEligibleItems();
    const eligibleSubtotal = eligibleItems.reduce((acc, item) => acc + (item.price * (item.quantity || 1)), 0);
    if (appliedDiscount.discountType === 'percentage') {
      discountCodeAmount = eligibleSubtotal * (appliedDiscount.discountValue / 100);
      discountType = 'percentage';
    } else if (appliedDiscount.discountType === 'fixed') {
      discountCodeAmount = Math.min(appliedDiscount.discountValue, eligibleSubtotal);
      discountType = 'fixed';
    } else if (appliedDiscount.discountType === 'bogo') {
      const sortedItems = [...eligibleItems].sort((a, b) => a.price - b.price);
      if (sortedItems.length > 0) discountCodeAmount = sortedItems[0].price * (sortedItems[0].quantity || 1);
      discountType = 'bogo';
    }
  }

  if (scratchPrizeRevealed && selectedScratchCard) {
    const prize = selectedScratchCard.prize;
    if (prize.includes('%')) {
      const percent = parseFloat(prize);
      setScratchDiscountAmount(baseSubtotal * (percent / 100));
    } else if (prize.includes('R')) {
      const amount = parseFloat(prize.replace('R', '').trim());
      setScratchDiscountAmount(Math.min(amount, baseSubtotal));
    } else if (prize.toLowerCase().includes('shipping')) {
      setScratchDiscountAmount(shippingDetails.shippingCost);
    }
  }

  const totalDiscount = voucherDiscount + discountCodeAmount + scratchDiscountAmount;
  const productPrice = Math.max(0, baseSubtotal - totalDiscount);

  const SECURITY_FEE = 50;
  const PLATFORM_FEE_PERCENTAGE = 0.10;

  const customerTotal = productPrice + SECURITY_FEE;
  const platformFee = productPrice * PLATFORM_FEE_PERCENTAGE;
  const sellerPayout = productPrice - platformFee;

  const isFreeShippingVoucher = activeVoucher && activeVoucher.includes('SHIPPING');
  const isGlobalFreeShipping = baseSubtotal >= 1000;
  const isBrandFreeShipping = activeBrandSettings?.freeShippingThreshold && baseSubtotal >= activeBrandSettings.freeShippingThreshold;

  const qualifiesForFreeShipping = isFreeShippingVoucher || isGlobalFreeShipping || isBrandFreeShipping || is100Off;
  const finalShippingCost = qualifiesForFreeShipping ? 0 : shippingDetails.shippingCost;

  const total = customerTotal + finalShippingCost;
  const totalToScoop = SECURITY_FEE + platformFee + finalShippingCost;

  // 🟢 Delivery methods: prefer what the seller has configured, fall back to
  // the built-in defaults for the house atelier (or any seller without methods).
  const sellerMethods = activeBrandSettings?.deliveryMethods?.filter((m: any) => m.enabled) || [];
  const isAtelier = activeBrandId === ATELIER_ID;
  const activeMethods =
    sellerMethods.length > 0
      ? sellerMethods
      : (isAtelier ? DEFAULT_DELIVERY_METHODS : []);

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) { setDiscountError("Please enter a discount code."); return; }
    setIsApplyingDiscount(true);
    setDiscountError(null);
    try {
      const discount = discounts.find(d => d.code.toUpperCase() === discountCode.toUpperCase().trim());
      if (!discount) { setDiscountError("Invalid discount code."); setIsApplyingDiscount(false); return; }
      if (appliedDiscount && appliedDiscount.id === discount.id) { setDiscountError("Discount already applied."); setIsApplyingDiscount(false); return; }
      if (discount.minPurchase && baseSubtotal < discount.minPurchase) { setDiscountError(`Minimum purchase of R${discount.minPurchase} required.`); setIsApplyingDiscount(false); return; }

      if (discount.appliesTo !== 'all') {
        const hasEligibleProduct = activeItems.some(item => {
          if (discount.appliesTo === 'specific_products') return discount.productIds?.includes(item.id);
          if (discount.appliesTo === 'collection') return item.collectionId === discount.collectionId;
          return false;
        });
        if (!hasEligibleProduct) { setDiscountError("This discount doesn't apply to any items in your cart."); setIsApplyingDiscount(false); return; }
      }

      setAppliedDiscount(discount);
      setDiscountCode('');
      showToast(`✅ ${discount.name} applied!`);
    } catch (error) {
      console.error("Error applying discount:", error);
      setDiscountError("Failed to apply discount.");
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    showToast("Discount removed.");
  };

  const scratchCard = async (card: ScratchCard) => {
    if (isScratching) return;
    setIsScratching(true);
    setSelectedScratchCard(card);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      let prizeAmount = 0;
      const prize = card.prize;
      if (prize.includes('%')) {
        const percent = parseFloat(prize);
        prizeAmount = baseSubtotal * (percent / 100);
      } else if (prize.includes('R')) {
        const amount = parseFloat(prize.replace('R', '').trim());
        prizeAmount = Math.min(amount, baseSubtotal);
      } else if (prize.toLowerCase().includes('shipping')) {
        prizeAmount = shippingDetails.shippingCost;
      }
      setScratchDiscountAmount(prizeAmount);
      setScratchPrizeRevealed(true);

      await updateDoc(doc(db, "scratchCards", card.id), {
        remainingScratches: increment(-1),
        scratchCount: increment(1)
      });
      const scratchQuery = query(collection(db, "scratchCards"), where("businessId", "==", activeBrandId), where("isActive", "==", true));
      const scratchSnap = await getDocs(scratchQuery);
      const updatedScratchCards = scratchSnap.docs.map(d => ({ id: d.id, ...d.data() } as ScratchCard)).filter(c => c.remainingScratches > 0);
      setScratchCards(updatedScratchCards);

      showToast(`🎉 You won: ${card.prize}!`);
    } catch (error) {
      console.error("Error scratching card:", error);
      showToast("Failed to scratch card. Please try again.");
    } finally {
      setIsScratching(false);
    }
  };

  // 🟢 SPLIT VALIDATION LOGIC
  const handleDeliveryNext = () => {
    if (!shippingDetails.deliveryMethod && total > 0) return showToast("Please select a routing method.");
    setStep('address');
  };

  const handleAddressNext = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!shippingDetails.name.trim()) return showToast("Full Name is required.");
    if (!shippingDetails.email.trim() || !shippingDetails.email.includes('@')) return showToast("Valid Email is required.");
    if (!shippingDetails.street.trim()) return showToast("Street Address is required.");
    if (!shippingDetails.city.trim()) return showToast("City is required.");
    if (!shippingDetails.code.trim()) return showToast("Postal Code is required.");
    if (!shippingDetails.phone.trim() || shippingDetails.phone.length < 5) return showToast("Valid Contact Number is required.");
    setStep('payment');
  };

  const resetPaymentState = () => {
    setIsProcessing(false);
    setIsMinimized(false);
  };

  const finalizeOrder = async (method: string, transactionId: string, finalPhone: string) => {
    try {
      const workspaceId = activeBrandSettings?.workspaceId || activeBrandId;
      const escrowMode = !sellerHasPayout || !sellerSubaccountCode;

      const orderData = {
        userId: profile?.uid || 'guest',
        businessId: activeBrandId,
        workspaceId: workspaceId,
        items: activeItems,
        productPrice: productPrice,
        baseSubtotal: baseSubtotal,
        totalDiscount: totalDiscount,
        securityFee: SECURITY_FEE,
        platformFee: platformFee,
        shippingCost: finalShippingCost,
        totalFees: totalToScoop,
        customerTotal: total,
        amount: sellerPayout,
        discountCode: appliedDiscount?.code || null,
        discountType: discountType,
        scratchPrize: selectedScratchCard?.prize || null,
        voucherUsed: activeVoucher || null,
        shippingDetails: { ...shippingDetails, phone: finalPhone },
        customerEmail: shippingDetails.email,
        status: 'paid',
        paymentMethod: method,
        paymentReference: transactionId,
        payoutStatus: escrowMode ? 'escrow_held' : 'pending',
        escrowMode: escrowMode,
        subaccountCode: sellerSubaccountCode || null,
        sellerHasPayout: sellerHasPayout,
        fundsReleased: false,
        source: 'website',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const orderRef = await addDoc(collection(db, 'orders'), orderData);
      if (profile?.uid && activeVoucher) {
        await updateDoc(doc(db, 'users', profile.uid), { activeVoucher: null });
      }

      if (method === 'makapay') return orderRef.id;

      setPaymentSuccess(true);
      setIsProcessing(false);
      setIsMinimized(false);

      if (escrowMode) {
        showToast("Order confirmed! Funds held securely until seller completes payout setup.");
      } else {
        showToast("Payment successful! Funds sent to seller.");
      }

      setTimeout(() => {
        onCheckoutComplete({
          ...shippingDetails, method, transactionId, brandCleared: activeBrandId,
          sellerPayout, platformFee, securityFee: SECURITY_FEE, escrowMode, discountApplied: totalDiscount,
        });

        const updatedCart = { ...groupedCart };
        delete updatedCart[activeBrandId!];
        setGroupedCart(updatedCart);

        if (Object.keys(updatedCart).length > 0) {
          setActiveBrandId(Object.keys(updatedCart)[0]);
          setStep('delivery');
          setPaymentSuccess(false);
          setIsProcessing(false);
          setAppliedDiscount(null);
          setScratchPrizeRevealed(false);
          setSelectedScratchCard(null);
        } else {
          onClose();
        }
      }, 2500);

      return orderRef.id;

    } catch (error) {
      console.error("Order Fulfillment Error:", error);
      setIsProcessing(false);
      setIsMinimized(false);
      showToast("Payment cleared, but logging failed. Ref: " + transactionId);
      return null;
    }
  };

  useEffect(() => {
    if (step === 'payment' && selectedPaymentMethod === 'paypal' && window.paypal && !isProcessing) {
      const container = document.getElementById('paypal-button-container');
      if (container) {
        container.innerHTML = '';
        const usdTotal = (total / 18.5).toFixed(2);
        window.paypal.Buttons({
          style: { layout: 'vertical', color: 'black', shape: 'rect', label: 'paypal' },
          createOrder: (data: any, actions: any) => {
            return actions.order.create({
              purchase_units: [{
                amount: { value: usdTotal, currency_code: 'USD' },
                description: `Order from ${activeBrandSettings?.businessName || 'Scoop'}`,
                custom_id: `ORD_${activeBrandId}_${Date.now()}`,
              }],
              application_context: { shipping_preference: 'NO_SHIPPING' }
            });
          },
          onApprove: (data: any, actions: any) => {
            setIsProcessing(true);
            return actions.order.capture().then(async (details: any) => {
              if (details.status === 'COMPLETED') {
                showToast("✅ PayPal payment successful!");
                let finalPhone = shippingDetails.phone;
                if (finalPhone.startsWith('0')) finalPhone = finalPhone.substring(1);
                const databaseReadyPhone = `${phoneCode}${finalPhone}`;
                await finalizeOrder('paypal', details.id, databaseReadyPhone);
              } else {
                resetPaymentState();
                showToast("PayPal payment failed.");
              }
            });
          },
          onCancel: () => {
            resetPaymentState();
            showToast("PayPal payment cancelled.");
          },
          onError: (err: any) => {
            resetPaymentState();
            showToast("PayPal error: " + (err.message || 'Unknown error'));
          }
        }).render('#paypal-button-container');
      }
    }
  }, [step, selectedPaymentMethod, total, shippingDetails, phoneCode, activeBrandId, activeBrandSettings]);

  const handlePaymentSubmit = async () => {
    setIsProcessing(true);
    let finalPhone = shippingDetails.phone;
    if (finalPhone.startsWith('0')) finalPhone = finalPhone.substring(1);
    const databaseReadyPhone = `${phoneCode}${finalPhone}`;

    if (total === 0) {
      setTimeout(() => finalizeOrder('voucher', `FREE-${Date.now()}`, databaseReadyPhone), 1000);
      return;
    }

    if (selectedPaymentMethod === 'makapay') {
      const orderId = `ORD_${activeBrandId}_${Date.now()}`;
      try {
        const workspaceId = activeBrandSettings?.workspaceId || activeBrandId;
        const escrowMode = !sellerHasPayout || !sellerSubaccountCode;

        const orderRef = await addDoc(collection(db, 'orders'), {
          userId: profile?.uid || 'guest',
          businessId: activeBrandId,
          workspaceId: workspaceId,
          items: activeItems,
          customerTotal: total,
          customerEmail: shippingDetails.email,
          shippingDetails: { ...shippingDetails, phone: databaseReadyPhone },
          status: 'pending',
          paymentMethod: 'makapay',
          paymentReference: orderId,
          payoutStatus: escrowMode ? 'escrow_held' : 'pending',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        setPendingOrderId(orderRef.id);
        setIsMinimized(true);
        showToast("Generating secure MakaPay invoice...");

        const returnUrl = `${window.location.origin}${window.location.pathname}?orderId=${orderRef.id}&status=success`;
        const apiUrl = getEnvVar('API_URL') || 'https://africa-south1-scoop-streetwear.cloudfunctions.net/api';

        const response = await fetch(`${apiUrl}/create-makapay-invoice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: total.toFixed(2),
            currency: 'ZAR',
            order_id: orderRef.id,
            url_return: returnUrl,
            customer_email: shippingDetails.email,
            metadata: { brandId: activeBrandId }
          })
        });

        if (!response.ok) throw new Error("Backend API not found. Please check deployment.");

        const data = await response.json();
        if (data.checkoutUrl) window.location.href = data.checkoutUrl;
        else throw new Error("Invalid response from server");

      } catch (err: any) {
        console.error('MakaPay error:', err);
        resetPaymentState();
        showToast(err.message);
      }
      return;
    }

    if (!paystackLoaded) {
      if (!window.PaystackPop) {
        showToast("Gateway loading. Please wait...");
        resetPaymentState();
        return;
      }
    }

    setIsMinimized(true);
    showToast("Opening secure payment...");

    const timeoutId = setTimeout(() => {
      resetPaymentState();
      showToast("Payment taking longer than expected. Please try again.");
    }, 600000);

    try {
      const publicKey = getPaystackKey();
      if (!publicKey) throw new Error("Paystack public key not configured.");

      const reference = `ORD_${activeBrandId}_${Date.now()}_${Math.random().toString(36).substring(7)}`;

      const transactionOptions: any = {
        key: publicKey,
        email: shippingDetails.email,
        amount: Math.round(total * 100),
        currency: 'ZAR',
        ref: reference,
        metadata: {
          orderId: reference,
          brandId: activeBrandId,
          customerId: profile?.uid || 'guest',
          customerName: shippingDetails.name,
          customerPhone: databaseReadyPhone,
        },
        channels: ['card', 'bank', 'ussd', 'qr', 'mobile_money', 'bank_transfer'],
        callback: (response: any) => {
          clearTimeout(timeoutId);
          if (response.status === 'success' || response.message === 'Approved') {
            if (appliedDiscount) {
              updateDoc(doc(db, "discounts", appliedDiscount.id), { usageCount: increment(1) }).catch(console.error);
            }
            finalizeOrder('paystack', response.reference, databaseReadyPhone);
          } else {
            resetPaymentState();
            showToast("Payment Declined. Please try again.");
          }
        },
        onClose: () => {
          clearTimeout(timeoutId);
          if (!isProcessing) {
            resetPaymentState();
            showToast("Payment cancelled.");
          }
        }
      };

      if (sellerHasPayout && sellerSubaccountCode) {
        transactionOptions.subaccount = sellerSubaccountCode;
        transactionOptions.transaction_charge = Math.round(totalToScoop * 100);
      }

      if (!window.PaystackPop) throw new Error('Paystack is not loaded.');

      const handler = window.PaystackPop.setup(transactionOptions);
      if (!handler) throw new Error('Failed to initialize Paystack.');
      handler.openIframe();

    } catch (error: any) {
      clearTimeout(timeoutId);
      resetPaymentState();
      showToast("Payment error: " + error.message);
    }
  };

  const getValidImage = (item: any) => item.image || item.imageUrl || (item.images && item.images[0]) || 'https://images.unsplash.com/photo-1552346154-21d32810baa3?auto=format&fit=crop&q=80&w=800';

  // 🟢 REFINED, SMALLER PAYMENT BUTTONS FOR MOBILE
  const renderPaymentMethods = () => (
    <div>
      <div className="flex items-center gap-2 mb-3 border-b border-zinc-800 pb-2">
        <ShieldCheck size={14} className="text-zinc-500" />
        <label className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Payment Method</label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {/* Card */}
        <button
          onClick={() => setSelectedPaymentMethod('card')}
          className={`p-3 border flex flex-col items-center justify-center gap-1.5 transition-all rounded-xl ${
            selectedPaymentMethod === 'card'
              ? 'bg-white border-white text-black shadow-md'
              : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'
          }`}
        >
          <div className="flex items-center justify-center gap-1.5">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/visa.png?alt=media&token=5b1dac5d-04c1-4210-acdf-26dddf7a143b"
              alt="Visa"
              className="h-4 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <img
              src="https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/master-card.png?alt=media&token=6a81e0ed-8d2f-4f9c-95e1-db80fcc34a34"
              alt="Mastercard"
              className="h-4 w-auto object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
          <span className="font-black text-[9px] uppercase tracking-widest text-center leading-tight">Card / EFT</span>
        </button>

        {/* Apple Pay */}
        {isApplePaySupported && (
          <button
            onClick={() => setSelectedPaymentMethod('apple_pay')}
            className={`p-3 border flex flex-col items-center justify-center gap-1.5 transition-all rounded-xl ${
              selectedPaymentMethod === 'apple_pay'
                ? 'bg-black border-zinc-500 text-white shadow-md'
                : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-zinc-500 hover:text-white'
            }`}
          >
            <div className="h-4 flex items-center justify-center bg-white px-2 rounded-sm">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/ApplePayLogo.png?alt=media&token=a3b8ef0b-7755-4598-a6c1-37844db68d91"
                alt="Apple Pay"
                className="h-full object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <span className="font-black text-[9px] uppercase tracking-widest text-center leading-tight">Apple Pay</span>
          </button>
        )}

        {/* PayPal */}
        <button
          onClick={() => setSelectedPaymentMethod('paypal')}
          className={`p-3 border flex flex-col items-center justify-center gap-1.5 transition-all rounded-xl ${
            selectedPaymentMethod === 'paypal'
              ? 'bg-[#0070BA]/10 border-[#0070BA] text-[#0070BA]'
              : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-[#0070BA] hover:text-[#0070BA]'
          }`}
        >
          <img
            src="https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/PayPal_Logo_Icon_2014.svg?alt=media&token=0e15794f-bc8c-4773-ae49-2c6e321afc78"
            alt="PayPal"
            className="h-5 w-auto object-contain"
          />
          <span className="font-black text-[9px] uppercase tracking-widest text-center leading-tight">PayPal</span>
        </button>

        {/* MakaPay */}
        <button
          onClick={() => setSelectedPaymentMethod('makapay')}
          className={`p-3 border flex flex-col items-center justify-center gap-1.5 transition-all rounded-xl ${
            selectedPaymentMethod === 'makapay'
              ? 'bg-white border-white text-black shadow-md'
              : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-white hover:text-white'
          }`}
        >
          <img
            src="https://firebasestorage.googleapis.com/v0/b/scoop-streetwear.firebasestorage.app/o/icon-256x256.png?alt=media&token=709a184c-722b-4011-81e0-d0230afc8475"
            alt="MakaPay"
            className="h-5 w-auto object-contain rounded-full"
          />
          <span className="font-black text-[9px] uppercase tracking-widest text-center leading-tight">Crypto</span>
        </button>
      </div>
    </div>
  );

  const renderPriceBreakdown = () => (
    <div className="border border-zinc-800 p-4 rounded-xl bg-zinc-950 font-mono text-xs uppercase tracking-widest flex flex-col gap-2 relative overflow-hidden">
      {activeVoucher && <div className="absolute -top-10 -right-10 w-24 h-24 bg-cyan-500/20 blur-3xl pointer-events-none" />}

      <div className="flex items-center gap-2 mb-1 pb-1 border-b border-zinc-800">
        <Wallet size={14} className="text-zinc-500" />
        <span className="font-black text-[9px] tracking-[0.3em] text-white">Order Summary</span>
      </div>

      <div className="flex justify-between text-zinc-500 text-[10px]">
        <span>Items</span>
        <span className={totalDiscount > 0 ? 'line-through opacity-50' : 'text-white'}>
          ZAR {baseSubtotal.toFixed(2)}
        </span>
      </div>

      {voucherDiscount > 0 && (
        <div className="flex justify-between text-green-400 text-[10px]">
          <span>Voucher</span>
          <span>- ZAR {voucherDiscount.toFixed(2)}</span>
        </div>
      )}

      {appliedDiscount && discountCodeAmount > 0 && (
        <div className="flex justify-between text-cyan-400 text-[10px]">
          <span>Code {appliedDiscount.code}</span>
          <span>- ZAR {discountCodeAmount.toFixed(2)}</span>
        </div>
      )}

      {scratchPrizeRevealed && selectedScratchCard && scratchDiscountAmount > 0 && (
        <div className="flex justify-between text-purple-400 text-[10px]">
          <span>Scratch Win</span>
          <span>- ZAR {scratchDiscountAmount.toFixed(2)}</span>
        </div>
      )}

      <div className="flex justify-between text-zinc-500 text-[10px]">
        <span>Product Total</span>
        <span className="text-white">ZAR {productPrice.toFixed(2)}</span>
      </div>

      <div className="flex justify-between text-yellow-400 text-[10px] border-b border-zinc-800 pb-1">
        <span className="flex items-center gap-1">
          <ShieldCheck size={10} /> Security Fee
        </span>
        <span>R{SECURITY_FEE.toFixed(2)}</span>
      </div>

      <div className="flex justify-between text-zinc-500 text-[10px] items-center">
        <span>Shipping</span>
        <div className="text-right">
          {qualifiesForFreeShipping ? (
            <span className="text-cyan-400">FREE</span>
          ) : (
            <span className="text-white">ZAR {finalShippingCost.toFixed(2)}</span>
          )}
        </div>
      </div>

      <div className="w-full border-t border-dashed border-zinc-800 my-1"></div>

      <div className="flex justify-between items-end text-white">
        <span className="font-black text-[10px]">Total Due</span>
        <span className="font-black text-xl italic tracking-tighter leading-none text-white">
          ZAR {total.toFixed(2)}
        </span>
      </div>
    </div>
  );

  const renderDiscountSection = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
        <Percent size={14} className="text-zinc-500" />
        <label className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Discounts</label>
      </div>

      {scratchCards.length > 0 && !scratchPrizeRevealed && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            {scratchCards.map(card => (
              <button
                key={card.id}
                onClick={() => scratchCard(card)}
                disabled={isScratching}
                className="p-3 border border-purple-500/30 bg-purple-950/20 rounded-xl hover:border-purple-500 transition-all flex flex-col items-center gap-1"
              >
                <Ticket size={20} className="text-purple-400" />
                <span className="text-white font-black text-[8px] uppercase tracking-widest">Scratch & Win</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {scratchPrizeRevealed && selectedScratchCard && (
        <div className="p-3 border border-purple-500 bg-purple-950/20 rounded-xl flex items-center justify-between">
          <div>
            <p className="text-purple-400 font-black text-xs">🎉 You Won: {selectedScratchCard.prize}</p>
          </div>
          <button onClick={() => { setScratchPrizeRevealed(false); setSelectedScratchCard(null); setScratchDiscountAmount(0); }} className="text-zinc-500 hover:text-white">
            <X size={14} />
          </button>
        </div>
      )}

      {!appliedDiscount && (
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="PROMO CODE"
            className="flex-1 bg-zinc-950 border border-zinc-800 p-3 rounded-xl text-white font-mono text-xs uppercase placeholder:text-zinc-600 focus:border-white focus:outline-none"
            value={discountCode}
            onChange={(e) => { setDiscountCode(e.target.value); setDiscountError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyDiscountCode(); } }}
          />
          <button
            onClick={applyDiscountCode}
            disabled={isApplyingDiscount || !discountCode.trim()}
            className="px-4 py-3 bg-white text-black font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-zinc-200 disabled:opacity-50"
          >
            {isApplyingDiscount ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
          </button>
        </div>
      )}
      {discountError && <p className="text-red-400 text-[10px] font-mono mt-1">{discountError}</p>}

      {appliedDiscount && (
        <div className="p-3 border border-green-500/50 bg-green-950/20 rounded-xl flex items-center justify-between">
          <div>
            <p className="text-green-400 font-black text-xs">{appliedDiscount.name} ({appliedDiscount.code})</p>
          </div>
          <button onClick={removeDiscount} className="text-zinc-500 hover:text-red-500">
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );

  if (isMinimized && !paymentSuccess && !pendingOrderId) {
    return createPortal(
      <div className="fixed bottom-0 left-0 right-0 z-[100] bg-black/90 backdrop-blur-md border-t-2 border-zinc-700 p-5 flex items-center justify-between animate-in slide-in-from-bottom-full shadow-[0_-10px_30px_rgba(0,0,0,0.8)]">
        <div className="flex items-center gap-4 pointer-events-none">
          <Loader2 size={24} className="animate-spin text-white" />
          <div>
            <span className="text-white font-black text-xs uppercase tracking-widest">Processing Payment...</span>
          </div>
        </div>
        <button
          onClick={() => { resetPaymentState(); showToast("Payment cancelled."); }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-black text-xs uppercase tracking-widest rounded-lg"
        >
          Cancel
        </button>
      </div>,
      document.body
    );
  }

  if (!isOpen || cart.length === 0) return null;

  return createPortal(
    <div className={`fixed inset-0 z-[500] flex flex-col justify-end lg:justify-center items-center animate-in fade-in duration-300`}>

      {toastMessage && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-[9999] bg-red-600 border border-red-500 text-white px-6 py-4 rounded-full font-black text-[10px] uppercase tracking-widest flex items-center gap-3 shadow-2xl animate-in slide-in-from-top-6">
          <AlertCircle size={16} /> {toastMessage}
        </div>
      )}

      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />

      <div className="w-full h-[95dvh] lg:w-[1200px] lg:h-[85vh] bg-black rounded-t-[2rem] lg:rounded-[2rem] lg:border border-zinc-800 flex flex-col lg:flex-row overflow-hidden shadow-2xl relative z-10 animate-in slide-in-from-bottom-full lg:slide-in-from-bottom-0 lg:zoom-in-95">

        {/* MOBILE HEADER */}
        <div className="lg:hidden px-6 py-4 border-b border-zinc-800 flex items-center justify-between bg-black z-10 shrink-0">
          <button
            onClick={() => {
              if (step === 'address') setStep('delivery');
              else if (step === 'payment') setStep('address');
              else onClose();
            }}
            className="flex items-center gap-1 text-zinc-400 hover:text-white"
          >
            {step === 'delivery' || paymentSuccess ? <X size={20} /> : <ChevronLeft size={20} />}
          </button>
          <h2 className="text-lg font-black uppercase italic tracking-tighter">
            {step === 'delivery' ? 'Delivery' : step === 'address' ? 'Address' : 'Payment'}
          </h2>
          <div className="w-[20px]" />
        </div>

        {/* LEFT COLUMN: DISPATCH */}
        <div className="hidden lg:flex w-[35%] bg-zinc-950 border-r border-zinc-800 flex-col shrink-0">
          <div className="p-8 border-b border-zinc-800 bg-black">
            <h2 className="text-2xl font-black text-white uppercase italic flex items-center gap-3"><Store size={24} /> Dispatch</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {Object.keys(groupedCart).map((brandId) => {
              const sellerInfo = activeBrandId === brandId ? activeBrandSettings : null;
              // 🟢 Show "Scoop & Stone" for the atelier even if the doc is missing.
              const isAtelierRow = brandId === ATELIER_ID;
              const sellerDisplayName =
                sellerInfo?.businessName
                || sellerInfo?.displayName
                || (isAtelierRow ? ATELIER_NAME : 'Seller');
              return (
                <button
                  key={brandId}
                  onClick={() => { if(activeBrandId !== brandId && !isProcessing) setActiveBrandId(brandId); }}
                  disabled={isProcessing}
                  className={`w-full p-5 text-left border-2 transition-all flex justify-between items-center rounded-2xl ${
                    activeBrandId === brandId ? 'border-white bg-white text-black' : 'border-zinc-800 bg-black text-zinc-500'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-zinc-900 flex items-center justify-center">
                      {sellerInfo?.logoUrl ? <img src={sellerInfo.logoUrl} className="w-full h-full object-cover grayscale" /> : <User size={14} />}
                    </div>
                    <p className="font-black uppercase tracking-widest text-sm truncate max-w-[150px]">{sellerDisplayName}</p>
                  </div>
                  {activeBrandId === brandId && <ArrowRight size={20} />}
                </button>
              )
            })}
          </div>
        </div>

        {/* RIGHT COLUMN: LOGISTICS & PAYMENT */}
        <div className="flex-1 flex flex-col h-full bg-black relative">
          <button onClick={onClose} className="hidden lg:flex absolute top-8 right-8 p-3 text-zinc-500 hover:text-white z-20">
            <X size={24} />
          </button>

          {paymentSuccess ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12 text-center animate-in zoom-in">
              <CheckCircle className="w-24 h-24 text-white mb-6" strokeWidth={1.5} />
              <h2 className="text-3xl font-black text-white uppercase italic tracking-tighter">Order Confirmed</h2>
              <p className="font-mono text-xs text-zinc-400 uppercase tracking-widest mt-2">Your order has been routed to the seller.</p>
            </div>
          ) : (
            <>
              {/* DESKTOP HEADER */}
              <div className="hidden lg:block shrink-0 p-12 pb-6 border-b border-zinc-800">
                <div className="flex items-center gap-4 mb-2 h-6">
                  {step !== 'delivery' && (
                    <button onClick={() => step === 'payment' ? setStep('address') : setStep('delivery')} className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors">
                      <ChevronLeft size={16} /> <span className="text-[10px] font-bold uppercase tracking-widest">Back</span>
                    </button>
                  )}
                </div>
                <h2 className="text-4xl font-black uppercase italic tracking-tighter text-white">
                  {step === 'delivery' ? '01 / Routing' : step === 'address' ? '02 / Destination' : '03 / Authorization'}
                </h2>
              </div>

              {/* SCROLLABLE CONTENT AREA */}
              <div className="flex-1 overflow-y-auto p-6 lg:p-12 pb-10 scrollbar-hide">

                {/* STEP 1: DELIVERY METHOD */}
                {step === 'delivery' && (
                  <div className="space-y-6 max-w-xl mx-auto lg:mx-0 animate-in slide-in-from-right">
                    <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
                      <Truck size={16} className="text-zinc-500" />
                      <label className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Select Routing</label>
                    </div>

                    {isGlobalFreeShipping && (
                      <div className="p-3 border border-cyan-500/50 bg-cyan-950/20 text-cyan-400 font-mono text-[10px] flex items-center justify-center gap-2 rounded-xl">
                        <Gift size={14} /> FREE SHIPPING APPLIED
                      </div>
                    )}

                    {activeMethods.length === 0 ? (
                      <div className="p-4 border border-red-900 text-red-500 font-mono text-xs bg-red-950/20 rounded-xl">
                        Terminal Offline: Seller has no delivery options.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3">
                        {activeMethods.map((method: any) => (
                          <button
                            key={method.id}
                            onClick={() => setShippingDetails({ ...shippingDetails, deliveryMethod: method.label, shippingCost: method.price })}
                            className={`p-4 border flex items-center gap-4 transition-all rounded-xl ${shippingDetails.deliveryMethod === method.label ? 'bg-white text-black border-white' : 'bg-zinc-950 border-zinc-800 text-zinc-500'}`}
                          >
                            {method.id === 'paxi' ? <Package size={24} /> : <Truck size={24} />}
                            <div className="text-left flex-1">
                              <div className="font-black text-xs uppercase tracking-widest">{method.label}</div>
                              {method.duration && <div className="text-[10px] font-mono opacity-70">⏱ {method.duration}</div>}
                            </div>
                            <div className="text-xs font-black font-mono">
                              {qualifiesForFreeShipping ? 'FREE' : `R${method.price.toFixed(2)}`}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* STEP 2: ADDRESS */}
                {step === 'address' && (
                  <form id="address-form" onSubmit={handleAddressNext} className="space-y-6 max-w-xl mx-auto lg:mx-0 animate-in slide-in-from-right">
                    <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
                      <MapPin size={16} className="text-zinc-500" />
                      <label className="text-[10px] font-black text-white uppercase tracking-[0.3em]">Destination Data</label>
                    </div>

                    <div className="space-y-3">
                      <input required placeholder="FULL NAME" className="w-full bg-zinc-950 p-4 rounded-xl border border-zinc-800 text-white font-mono text-xs uppercase focus:border-white focus:outline-none" value={shippingDetails.name} onChange={e => setShippingDetails({...shippingDetails, name: e.target.value})} />
                      <input required type="email" placeholder="EMAIL (FOR RECEIPTS)" className="w-full bg-zinc-950 p-4 rounded-xl border border-zinc-800 text-white font-mono text-xs uppercase focus:border-white focus:outline-none" value={shippingDetails.email} onChange={e => setShippingDetails({...shippingDetails, email: e.target.value})} />

                      <div className="relative">
                        <input ref={streetInputRef} required placeholder="STREET ADDRESS" className="w-full bg-zinc-950 p-4 pl-10 rounded-xl border border-zinc-800 text-white font-mono text-xs uppercase focus:border-white focus:outline-none" value={shippingDetails.street} onChange={e => setShippingDetails({...shippingDetails, street: e.target.value})} />
                        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                      </div>

                      <div className="flex gap-3">
                        <input required placeholder="CITY" className="w-1/2 bg-zinc-950 p-4 rounded-xl border border-zinc-800 text-white font-mono text-xs uppercase focus:border-white focus:outline-none" value={shippingDetails.city} onChange={e => setShippingDetails({...shippingDetails, city: e.target.value})} />
                        <input required placeholder="POSTAL CODE" className="w-1/2 bg-zinc-950 p-4 rounded-xl border border-zinc-800 text-white font-mono text-xs uppercase focus:border-white focus:outline-none" value={shippingDetails.code} onChange={e => setShippingDetails({...shippingDetails, code: e.target.value})} />
                      </div>

                      <div className="flex">
                        <select className="bg-zinc-900 px-3 border border-zinc-800 border-r-0 rounded-l-xl text-zinc-400 font-mono text-xs focus:outline-none" value={phoneCode} onChange={e => setPhoneCode(e.target.value)}>
                          <option value="+27">+27</option>
                          <option value="+1">+1</option>
                          <option value="+44">+44</option>
                        </select>
                        <input required placeholder="PHONE NUMBER" type="tel" className="w-full bg-zinc-950 p-4 rounded-r-xl border border-zinc-800 text-white font-mono text-xs uppercase focus:border-white focus:outline-none" value={shippingDetails.phone} onChange={e => setShippingDetails({...shippingDetails, phone: e.target.value.replace(/\D/g, '')})} />
                      </div>
                    </div>
                  </form>
                )}

                {/* STEP 3: PAYMENT */}
                {step === 'payment' && (
                  <div className="space-y-6 animate-in slide-in-from-right max-w-xl mx-auto lg:mx-0">
                    {renderPaymentMethods()}
                    {renderPriceBreakdown()}
                    {renderDiscountSection()}
                  </div>
                )}
              </div>

              {/* DYNAMIC BOTTOM ACTION BAR - Padding expanded for safe areas & PayPal */}
              <div className="shrink-0 w-full p-4 pb-8 lg:p-8 border-t border-zinc-800 bg-black z-20">
                {step === 'delivery' && (
                  <button onClick={handleDeliveryNext} className="w-full lg:max-w-xl mx-auto lg:mx-0 py-4 bg-white text-black font-black text-xs uppercase tracking-widest rounded-full hover:bg-zinc-200">
                    Continue to Address
                  </button>
                )}

                {step === 'address' && (
                  <button onClick={() => document.getElementById('address-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))} className="w-full lg:max-w-xl mx-auto lg:mx-0 py-4 bg-white text-black font-black text-xs uppercase tracking-widest rounded-full hover:bg-zinc-200">
                    Proceed to Payment
                  </button>
                )}

                {step === 'payment' && selectedPaymentMethod === 'paypal' ? (
                  // 🟢 PAYPAL CONTAINER WITH DYNAMIC HEIGHT TO PREVENT CUTOFF
                  <div className="w-full lg:max-w-xl mx-auto lg:mx-0 relative z-[9999] min-h-[55px]">
                    <div id="paypal-button-container" className="w-full"></div>
                  </div>
                ) : step === 'payment' && (
                  <button onClick={handlePaymentSubmit} disabled={isProcessing} className="w-full lg:max-w-xl mx-auto lg:mx-0 py-4 bg-white text-black font-black text-xs uppercase tracking-widest rounded-full flex justify-center items-center gap-2">
                    {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <>Pay ZAR {total.toFixed(2)} <Lock size={14} /></>}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};